/**
 * SCD Lot 1 — Helpers purs de la boucle retail.
 * Consommés par retailSlice (tick) et par les écrans Assortiment / Achats.
 * Données métier : /data/dgs/*.json (fictives, en français).
 */
import secteursData from '../data/dgs/secteurs.json';
import modelesData from '../data/dgs/modeles_entreprise.json';
import fournisseursData from '../data/dgs/fournisseurs.json';
import pdvData from '../data/dgs/points_de_vente.json';
import zonesData from '../data/dgs/zones_chalandise.json';

/* Migration 30/06 — le secteur Alimentaire (périssable) est remplacé par la Quincaillerie. */
const MIGRATION_SECTEURS = { alimentaire_proximite: 'quincaillerie' };
const secId = (id) => MIGRATION_SECTEURS[id] || id;

export const getSecteur = (id) => secteursData.secteurs.find(s => s.id === secId(id)) || null;
export const getPositionnement = (id) => secteursData.positionnements[id] || secteursData.positionnements.coeur;
export const getModele = (id) => modelesData.modeles.find(m => m.id === id) || modelesData.modeles[0];
export const getAllSecteurs = () => secteursData.secteurs;
export const getAllPositionnements = () => Object.values(secteursData.positionnements);
export const getAllModeles = () => modelesData.modeles;
export const getPdvForSecteur = (secteurId) => pdvData.points_de_vente.find(p => p.secteur === secId(secteurId)) || pdvData.points_de_vente[0];
export const getZone = (id) => zonesData.zones.find(z => z.id === id) || zonesData.zones[0];
export const getFournisseursBySecteur = (secteurId) => fournisseursData.fournisseurs.filter(f => f.secteur === secId(secteurId));
export const getFournisseur = (id) => fournisseursData.fournisseurs.find(f => f.id === id) || null;
export const getFamille = (secteurId, famId) => {
  const sec = getSecteur(secteurId);
  return sec ? sec.familles.find(f => f.id === famId) || null : null;
};

/** Prix de vente conseillé HT = prix d'achat × coefficient famille × modificateur positionnement. */
export const prixConseille = (famille, positionnementId) => {
  const pos = getPositionnement(positionnementId);
  return Math.round(famille.prix_achat * famille.coefficient * (pos.coef_mult || 1) * 100) / 100;
};

/**
 * Ajustement de demande lié au prix (élasticité-prix).
 * ratio = prix pratiqué / prix conseillé ; ajustement = ratio^elasticite.
 * Clampé [0.25, 2.5] pour éviter les dérives.
 */
export const priceAdjustment = (prix, conseille, elasticite) => {
  if (!conseille || conseille <= 0 || !prix || prix <= 0) return 1;
  const ratio = prix / conseille;
  const adj = Math.pow(ratio, elasticite);
  return Math.min(2.5, Math.max(0.25, adj));
};

/**
 * Demande de base (unités/jour) pour une famille, HORS ajustement prix,
 * bruit aléatoire et week-end. Mois : 1-12.
 */
export const demandeBaseJour = (famille, secteurId, positionnementId, modeleId, month = 1) => {
  const sec = getSecteur(secteurId);
  if (!sec || !famille) return 0;
  const pos = getPositionnement(positionnementId);
  const mod = getModele(modeleId);
  const zone = getZone(getPdvForSecteur(secteurId).zone_chalandise_id);
  const saison = famille.saisonnalite?.[(month - 1) % 12] ?? 1;
  return zone.flux_pietons_jour
    * sec.captation
    * sec.conversion_base
    * sec.articles_par_achat
    * famille.part_demande
    * saison
    * (pos.trafic_mult || 1)
    * (pos.conversion_mult || 1)
    * (mod.trafic_mult || 1);
};

/** Remise volume applicable pour une quantité chez un fournisseur (en %).
 *  Relation tendue (< 30) : le fournisseur supprime les remises volume. */
export const remisePourQty = (fournisseur, qty, relation = 50) => {
  if (!fournisseur) return 0;
  if (relation < 30) return 0;
  let remise = 0;
  (fournisseur.remise_volume || []).forEach(p => {
    if (qty >= p.min_qty) remise = Math.max(remise, p.remise_pct);
  });
  return remise;
};

/* ── Relations fournisseurs (30/06) — score 0-100, départ 50 ──
 *  +2 par livraison réussie (+1 bonus grosse commande), −6 par retard,
 *  −4 par aléa. ≥ 70 : remise fidélité −3 % + livraison prioritaire (−1 j).
 *  < 30 : retards plus fréquents (×1,5) et fin des remises volume. */
export const RELATION_DEFAUT = 50;
export const REL_LIVRAISON = 2;
export const REL_GROSSE_COMMANDE = 1;
export const REL_RETARD = -6;
export const REL_ALEA = -4;
export const ALEA_PROBA_SUR_RETARD = 0.2;

export const getRelationFournisseur = (retail, fournisseurId) =>
  retail?.relationsFournisseurs?.[fournisseurId] ?? RELATION_DEFAUT;

export const relationNiveau = (score) => {
  if (score >= 70) return { id: 'partenaire', label: 'Partenaire', fidelitePct: 3, delaiBonus: 1, retardMult: 0.6 };
  if (score < 30) return { id: 'tendue', label: 'Tendue', fidelitePct: 0, delaiBonus: 0, retardMult: 1.5 };
  return { id: 'correcte', label: 'Correcte', fidelitePct: 0, delaiBonus: 0, retardMult: 1 };
};

/** Probabilité qu'une livraison soit retardée (fiabilité modulée par la relation). */
export const probaRetardLivraison = (fournisseur, relation = RELATION_DEFAUT) =>
  Math.min(0.6, ((100 - (fournisseur?.fiabilite_pct ?? 95)) / 100) * relationNiveau(relation).retardMult);

/** Le fournisseur couvre-t-il cette gamme ? (absent = toutes). */
export const fournisseurCouvre = (fournisseur, famId) =>
  !fournisseur?.familles || fournisseur.familles.includes(famId);

/** Seuil de « grosse commande » = palier de remise le plus élevé. */
export const grosseCommandeSeuil = (fournisseur) => {
  const tiers = fournisseur?.remise_volume || [];
  return tiers.length ? Math.max(...tiers.map(t => t.min_qty)) : Infinity;
};

/** Prix unitaire net HT (coef fournisseur × remise volume × centrale × fidélité relation). */
export const prixUnitaireNet = (famille, fournisseur, qty, modeleId, relation = RELATION_DEFAUT) => {
  const mod = getModele(modeleId);
  const remiseVol = remisePourQty(fournisseur, qty, relation);
  const remiseCentrale = mod.remise_fournisseur_pct || 0;
  const fidelite = relationNiveau(relation).fidelitePct;
  const net = famille.prix_achat * (fournisseur?.prix_coef || 1)
    * (1 - remiseVol / 100) * (1 - remiseCentrale / 100) * (1 - fidelite / 100);
  return Math.round(net * 100) / 100;
};

/** Couverture de stock en jours (stock / moyenne des ventes 7 derniers jours). */
export const couvertureJours = (stockQty, ventes7 = []) => {
  const arr = ventes7.filter(v => Number.isFinite(v));
  const avg = arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  if (avg <= 0.05) return stockQty > 0 ? 999 : 0;
  return Math.round(stockQty / avg);
};

/** Seuil de surstock (jours de couverture) au-delà duquel la démarque commence. */
export const SEUIL_SURSTOCK_JOURS = 45;
/** Part du stock excédentaire dépréciée chaque jour en cas de surstock. */
export const TAUX_DEMARQUE_JOUR = 0.01;

/* ════════════════ SCD Lot 2 — RH & fiscalité retail ════════════════ */
import metiersData from '../data/dgs/metiers_retail.json';

export const CHARGES_PATRONALES = metiersData.charges_patronales || 1.42;
export const getMetiers = (secteurId) => metiersData.metiers.filter(m => !m.secteur || m.secteur === secteurId);
export const getMetier = (id) => metiersData.metiers.find(m => m.id === id) || null;
export const CONTRATS_SPECIAUX = metiersData.contrats_speciaux;
export const OPTIONS_PLANNING = metiersData.options_planning;
export const FORMATION = metiersData.formation;

/** Frais de carte bancaire : 1,2 % sur 60 % du CA (part payée en CB). */
export const TAUX_FRAIS_CB = 0.012;
export const PART_CB = 0.6;
/** TASCOM pédagogique : au-delà de 150 m², 6 €/m²/an (payée au 1er janvier). */
export const TASCOM_SEUIL_M2 = 150;
export const TASCOM_EUR_M2_AN = 6;
/** Indexation annuelle du bail commercial (+2 %/an au 1er janvier). */
export const INDEXATION_BAIL = 1.02;

/** Efficacité d'un employé (formation ×1.25, alternant ×0.5, en formation = 0). */
export const efficaciteEmploye = (emp, totalDays = 0) => {
  if (emp.enFormationJusqua && emp.enFormationJusqua > totalDays) return 0;
  let eff = emp.contrat === 'alternance' ? (CONTRATS_SPECIAUX.alternant.efficacite || 0.5) : 1;
  if (emp.forme) eff *= FORMATION.bonus_efficacite || 1.25;
  return eff;
};

/**
 * Agrège les effets de l'équipe sur la boucle retail.
 * Retourne { conversionBonus, traficBonus, panierBonus, tauxRecupRupture,
 *            demarqueMult, capaciteCaisse, masseSalarialeJour, effectif }.
 */
export const computeEquipeEffects = (retail, totalDays = 0) => {
  const equipe = retail?.equipe || { employes: [], options: {} };
  const emps = (equipe.employes || []).filter(e => !e.finJour || e.finJour > totalDays);
  const opts = equipe.options || {};
  const eff = (metierId) => emps
    .filter(e => e.metierId === metierId)
    .reduce((a, e) => a + efficaciteEmploye(e, totalDays), 0);

  const vendeurs = eff('vendeur');
  const conversionBonus = Math.min(0.15, vendeurs * 0.05)
    + Math.min(0.06, eff('visual_merchandiser') * 0.06)
    + Math.min(0.04, eff('directeur_magasin') * 0.04)
    + (retail?.secteur === 'caviste' ? Math.min(0.14, eff('caviste_conseil') * 0.07) : 0)
    + (opts.primes ? (OPTIONS_PLANNING.primes.conversion_bonus || 0.03) : 0);
  // Management : au-delà de 5 employés sans directeur → malus.
  const malusManagement = (emps.length > 5 && eff('directeur_magasin') === 0) ? -0.05 : 0;

  const traficBonus = Math.min(0.08, eff('community_manager') * 0.08);
  const panierBonus = retail?.secteur === 'caviste' ? Math.min(0.10, eff('caviste_conseil') * 0.05) : 0;
  const tauxRecupRupture = Math.min(0.5, eff('employe_reserve') * 0.3 + eff('responsable_rayon') * 0.2 * 0.5)
    + (eff('responsable_rayon') > 0 ? 0.1 : 0);
  const demarqueMult = eff('responsable_rayon') > 0 ? 0.5 : 1;
  const capaciteCaisse = Math.max(0, eff('hote_caisse')) * 120;

  let masseMensuelle = emps.reduce((a, e) => a + (e.salaire || 0) * CHARGES_PATRONALES, 0);
  if (opts.dimanche) masseMensuelle *= OPTIONS_PLANNING.dimanche.masse_mult;
  if (opts.nocturnes) masseMensuelle *= OPTIONS_PLANNING.nocturnes.masse_mult;
  if (opts.primes) masseMensuelle *= OPTIONS_PLANNING.primes.masse_mult;

  let caMult = 1;
  if (opts.dimanche) caMult *= OPTIONS_PLANNING.dimanche.ca_mult;
  if (opts.nocturnes) caMult *= OPTIONS_PLANNING.nocturnes.ca_mult;

  return {
    conversionBonus: conversionBonus + malusManagement,
    traficBonus,
    panierBonus,
    tauxRecupRupture: Math.min(0.6, tauxRecupRupture),
    demarqueMult,
    capaciteCaisse,
    caMult,
    masseSalarialeJour: masseMensuelle / 30,
    masseSalarialeMensuelle: Math.round(masseMensuelle),
    effectif: emps.length,
    sansDirecteur: malusManagement < 0,
  };
};

/** BFR simplifié = valeur du stock − dettes fournisseurs non payées. */
export const computeBfr = (retail) => {
  if (!retail) return { stock: 0, dettes: 0, bfr: 0 };
  const stock = Object.values(retail.stock || {}).reduce((a, s) => a + s.qty * s.cump, 0);
  const dettes = (retail.commandes || []).filter(c => !c.paye).reduce((a, c) => a + c.montant, 0);
  return { stock: Math.round(stock), dettes: Math.round(dettes), bfr: Math.round(stock - dettes) };
};
